import { useCountry, useLocale } from '@/components/Link'
import { PopoverGroupType } from '@/components/page-specific/catering/common/components/popover'
import {
  addressToLabelAndDetailLines,
  addressToSingleLine,
  countries,
  isValidCity,
  lookupAddressDetails,
  MIN_QUALITY,
} from '@/components/page-specific/catering/common/helpers/addresses'
import {
  SPLITS,
  useFeatureFlag,
} from '@/components/page-specific/catering/common/helpers/useFeatureFlag'
import { AddressErrors } from '@/components/page-specific/catering/common/types/enums/Errors.enum'
import { isNotNil } from '@/components/page-specific/catering/common/utils/typing'
import type {
  AccountLocation,
  Address,
  UserBasketSettingsLocationFragment,
} from '@/generated/graphql'
import { useLookupAddressLazyQuery } from '@/generated/graphql'
import { useApolloClient } from '@apollo/client'
import { basketLocationToAddress } from '@caterdesk/utils--gm-validation'
import PlaceIcon from '@mui/icons-material/Place'
import type { AutocompleteRenderInputParams, SxProps, Theme } from '@mui/material'
import {
  Autocomplete,
  InputAdornment,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'

import { compact } from 'lodash'
import type React from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { match } from 'ts-pattern'

export type Props = {
  onAddressChanged: (address: UserBasketSettingsLocationFragment) => Promise<void>
  initialValue?: boolean
  placeholder?: string
  allowAnyCity?: boolean
  error?: string
  availableAccountLocations?: Array<
    | ({
        __typename: 'AccountLocation'
      } & Pick<AccountLocation, 'id' | 'label' | 'address' | 'isBillingAddress'>)
    | null
  >
  selectedLocation: UserBasketSettingsLocationFragment | undefined
  sx?: SxProps<Theme>
  renderInput?: (
    params: AutocompleteRenderInputParams,
    state: AddressLookupInputState,
  ) => React.ReactNode
}

export type AddressLookupInputState = {
  placeholder: string
  errorText?: string
}

let timeout: ReturnType<typeof setTimeout> | null = null

function renderDefaultInput(
  params: AutocompleteRenderInputParams,
  { placeholder, errorText }: AddressLookupInputState,
) {
  return (
    <TextField
      {...params}
      placeholder={placeholder}
      autoComplete="off"
      size="small"
      error={Boolean(errorText)}
      helperText={errorText}
      InputProps={{
        ...params.InputProps,
        startAdornment: (
          <InputAdornment position="start">
            <PlaceIcon color="primary" />
          </InputAdornment>
        ),
      }}
    />
  )
}

const addressPopoverFormat = (
  addresses: (Address & { index?: number })[],
  prefix = '',
  countryCode?: string | null,
) =>
  addresses
    .map((address) => addressToLabelAndDetailLines(address, countryCode))
    .map(({ label, details, index }, i) => ({
      value: `${prefix}${index || i}`,
      children: (
        <>
          <p data-testid="address-search-result-title">{label}</p>
          {details}
        </>
      ),
    }))

const AddressLookup: React.FC<Props> = ({
  initialValue = true,
  placeholder,
  allowAnyCity = false,
  availableAccountLocations = [],
  selectedLocation,
  error,
  onAddressChanged,
  sx,
  renderInput = renderDefaultInput,
}) => {
  const theme = useTheme()
  const currentCountry = useCountry()
  const getLocale = useLocale()

  const { t } = useTranslation()

  if (!placeholder) {
    placeholder = t('marketplace:enterAStreetAddress')
  }

  const locale = getLocale !== 'en-gb' ? getLocale : undefined
  const useNewSavedAddresses = useFeatureFlag(SPLITS.USE_NEW_SAVED_ADDRESSED)
  const [lookupError, setLookupError] = useState('')

  const errorText = lookupError || error
  const [addressInput, setAddressInput] = useState(
    selectedLocation && initialValue
      ? addressToSingleLine(basketLocationToAddress(selectedLocation), locale?.split('-')[1])
      : '',
  )
  const apolloClient = useApolloClient()

  const [lookupAddress, lookupAddressResult] = useLookupAddressLazyQuery()

  const addressesFound = useMemo(
    () =>
      compact(lookupAddressResult?.data?.lookupAddress?.locations ?? [])
        .filter((location) => {
          const valid =
            location.address &&
            location.relevance >= MIN_QUALITY &&
            location.address.postcode &&
            location.address.street &&
            location.address.city !== location.address.street
          const allowSingaporeCities = !allowAnyCity && location.address.country === 'Singapore'
          const allowAustralianCities = !allowAnyCity && location.address.country === 'Australia'
          return allowAnyCity || allowSingaporeCities || allowAustralianCities
            ? valid
            : valid && isValidCity(location.address.city || '')
        })
        .map(({ address }, index) => ({
          ...address,
          autocompleteType: t('marketplace:addressesFound'),
          label: address.street,
          index,
        })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lookupAddressResult?.data?.lookupAddress, allowAnyCity],
  )

  let popoverData: PopoverGroupType[] =
    addressesFound.length > 0
      ? [{ items: addressPopoverFormat(addressesFound, '', locale?.split('-')[1]) }]
      : []

  const accountLocationData = useMemo(
    () => availableAccountLocations.filter(isNotNil).filter((loc) => !loc.isBillingAddress),
    [availableAccountLocations],
  )

  const [showAccountDropdown, setShowAccountDropdown] = useState(
    accountLocationData.length > 0 &&
      (selectedLocation?.__typename === 'AccountLocation' || !selectedLocation),
  )

  useEffect(() => {
    setShowAccountDropdown(
      accountLocationData.length > 0 &&
        (selectedLocation?.__typename === 'AccountLocation' || !selectedLocation),
    )
  }, [selectedLocation, accountLocationData.length])

  const accountLocationAddresses = useMemo(
    () =>
      accountLocationData
        .map(({ id, label, address }, index) => ({
          ...address,
          label,
          index,
          id,
          autocompleteType: t('marketplace:savedAddresses'),
        }))
        .filter(({ label, ...address }) =>
          addressToSingleLine({ label, ...address }, locale?.split('-')[1])
            .toLowerCase()
            .includes(addressInput.toLowerCase()),
        ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accountLocationData, addressInput, locale],
  )

  const options = useMemo(
    () => [...accountLocationAddresses, ...addressesFound],
    [accountLocationAddresses, addressesFound],
  )

  if (useNewSavedAddresses) {
    const searchResults =
      popoverData.length > 0 ? { ...popoverData[0], title: t('marketplace:addressesFound') } : null

    popoverData = []

    if (accountLocationAddresses.length > 0) {
      popoverData = [
        {
          title: t('marketplace:savedAddresses'),
          items: addressPopoverFormat(accountLocationAddresses, 'account-', locale?.split('-')[1]),
        },
      ]
    } else if (showAccountDropdown) {
      popoverData = [
        {
          items: [{ value: '', children: t('marketplace:noSavedAddressesMatch') }],
        },
      ]
    }

    if (showAccountDropdown) {
      popoverData.push({
        items: [
          {
            value: 'new',
            children: (
              <Typography
                sx={{
                  color: 'primary.main',
                  textDecoration: 'underline',
                }}>
                {t('marketplace:yourSavedAddresses')}
              </Typography>
            ),
          },
        ],
      })
    } else if (searchResults) {
      popoverData.push(searchResults)
    }
  }

  if (!useNewSavedAddresses && accountLocationAddresses.length > 0) {
    popoverData = [
      {
        title: t('marketplace:yourSavedAddresses'),
        items: addressPopoverFormat(accountLocationAddresses, 'account-', locale?.split('-')[1]),
      },
      ...popoverData,
    ]
  }

  useEffect(() => {
    const hasLookupData = Boolean(lookupAddressResult?.data?.lookupAddress)
    if (
      lookupError === AddressErrors.INVALID_ADDRESS &&
      hasLookupData &&
      addressesFound.length > 0
    ) {
      setLookupError('')
    } else if (!lookupError && hasLookupData && addressInput.length > 2 && !addressesFound.length) {
      setLookupError(AddressErrors.INVALID_ADDRESS)
    }
  }, [lookupError, lookupAddressResult?.data?.lookupAddress, addressesFound.length, addressInput])

  const lookupAddressChange = useCallback(
    // @ts-expect-error unknown
    (query) => {
      if (timeout) clearTimeout(timeout)
      if (!query) return

      timeout = setTimeout(() => {
        lookupAddress({
          variables: { query, countries, useGoogle: true, allowAnyCity, locale: locale || null },
        }).catch(() => undefined)
      }, 500)
    },
    [allowAnyCity, locale, lookupAddress],
  )

  const inputTextChange = (value: string) => {
    setAddressInput(value)
    lookupAddressChange(value)

    if (lookupError) {
      setLookupError('')
    }
  }

  const handleAddressSelected = async (value?: string) => {
    if (!value) {
      setLookupError(AddressErrors.INVALID_ADDRESS)
      return
    }

    if (value === 'new') {
      setShowAccountDropdown(false)
      return
    }

    let basketLocation = value.startsWith('account-')
      ? accountLocationData[Number.parseInt(value.split('-')[1], 10)]
      : addressesFound[Number.parseInt(value, 10)]

    const address = basketLocationToAddress(basketLocation)

    let { latitude, longitude } = address

    if (basketLocation.__typename === 'AccountLocation' && (!latitude || !longitude)) {
      const addressFound = await lookupAddressDetails(address, apolloClient, true, locale)

      if (addressFound) {
        latitude = addressFound.latitude
        longitude = addressFound.longitude
        basketLocation = {
          ...basketLocation,
          address: { ...basketLocation.address, latitude, longitude },
        }

        setShowAccountDropdown(true)
      }
    }

    if (!latitude || !longitude) {
      setLookupError(AddressErrors.INVALID_ADDRESS)
      return
    }

    setLookupError('')
    onAddressChanged(basketLocation)
    setAddressInput(addressToSingleLine(address, locale?.split('-')[1]))
  }

  return (
    <Autocomplete
      size="small"
      options={options}
      getOptionLabel={(option) => option.label || ''}
      filterOptions={(x) => x}
      loading={lookupAddressResult.loading}
      groupBy={(option) => option.autocompleteType}
      onInputChange={(_, value, reason) => {
        if (['input', 'clear'].includes(reason)) inputTextChange(value)
      }}
      inputValue={addressInput}
      onChange={(_, value) => {
        match(value)
          .with({ autocompleteType: t('marketplace:savedAddresses') }, (v) => {
            const isInCurrentLocale = v.country?.toLowerCase() === currentCountry.toLowerCase()

            if (isInCurrentLocale) {
              handleAddressSelected(`account-${v.index}`)
            }
          })
          .with({ autocompleteType: t('marketplace:addressesFound') }, (v) =>
            handleAddressSelected(v.index.toString()),
          )
          .otherwise(() => {})
      }}
      fullWidth
      renderInput={(params) => {
        const inputProps = {
          ...params.inputProps,
          'data-dd-privacy': 'allow',
          'data-testid': 'gm-address-input',
        }

        return renderInput({ ...params, inputProps }, { placeholder, errorText })
      }}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props

        const optionIdentifier =
          ('id' in option ? option.id : undefined) ??
          option.index ??
          option.label ??
          key ??
          option.autocompleteType

        const optionKey =
          option.autocompleteType === t('marketplace:savedAddresses')
            ? `saved-${optionIdentifier}`
            : option.autocompleteType === t('marketplace:addressesFound')
              ? `found-${optionIdentifier}`
              : `${option.autocompleteType}-${optionIdentifier}`

        const disabled =
          option.autocompleteType === t('marketplace:savedAddresses')
            ? option.country?.toLowerCase() !== currentCountry.toLowerCase()
            : false

        return (
          <ListItemButton key={optionKey} component="li" disabled={disabled} {...optionProps}>
            {match(option)
              .with({ autocompleteType: t('marketplace:savedAddresses') }, () => (
                <ListItemText
                  primary={
                    disabled ? (
                      <>
                        {option.label}{' '}
                        <span style={{ color: `${theme.palette.error.main}` }}>
                          (
                          {t('marketplace:changeRegionToFor', {
                            country: option.country?.toUpperCase(),
                          })}
                          )
                        </span>
                      </>
                    ) : (
                      option.label
                    )
                  }
                  secondary={addressToSingleLine({ ...option, label: null }, locale?.split('-')[1])}
                  data-cy="address-lookup-search-result-text"
                />
              ))
              .with({ autocompleteType: t('marketplace:addressesFound') }, () => (
                <ListItemText
                  primary={
                    <span style={{ color: `${theme.palette.text.primary}` }}>
                      {addressToSingleLine({ ...option, label: null }, locale?.split('-')[1])}
                    </span>
                  }
                  data-cy="address-lookup-search-result-text"
                />
              ))
              .otherwise(() => null)}
          </ListItemButton>
        )
      }}
      sx={[
        {
          mb: 2,
          '& .MuiOutlinedInput-root': {
            borderRadius: 4,
            fontSize: '1rem',
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      data-cy="address-lookup"
    />
  )
}

export default AddressLookup
